import { supabase } from "../lib/supabase";

export const BLOG_MEDIA_BUCKET = "blog-media";

export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return `file_${Date.now()}`;
  return fileName
    .replace(/\s+/g, "_")
    .replace(/[^\w.-]/g, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

export function getBlogMediaPublicUrl(path: string | null | undefined): string | null {
  if (!path || !path.trim()) return null;
  const cleanPath = path.trim().replace(/^\//, "");
  const { data } = supabase.storage.from(BLOG_MEDIA_BUCKET).getPublicUrl(cleanPath);
  return data.publicUrl || null;
}

export async function uploadBlogMediaImage(
  postId: string,
  file: File
): Promise<{ path: string } | { error: string }> {
  if (!file) return { error: "No file provided" };
  if (!file.type.startsWith("image/")) {
    return { error: "Only image files are allowed" };
  }

  const sanitized = sanitizeFileName(file.name);
  const filePath = `posts/${postId}/${Date.now()}_${sanitized}`;

  const { data, error } = await supabase.storage
    .from(BLOG_MEDIA_BUCKET)
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (error) {
    return { error: error.message };
  }
  return { path: data.path };
}

export async function removeBlogMediaObject(
  path: string
): Promise<{ error: string | null }> {
  const cleanPath = path.trim().replace(/^\//, "");
  if (!cleanPath) return { error: null };

  const { error } = await supabase.storage
    .from(BLOG_MEDIA_BUCKET)
    .remove([cleanPath]);

  return { error: error?.message ?? null };
}

/** Best-effort delete of all objects under posts/{postId}/ */
export async function removeBlogPostMediaFolder(postId: string): Promise<void> {
  const prefix = `posts/${postId}`;
  const { data: listed, error } = await supabase.storage
    .from(BLOG_MEDIA_BUCKET)
    .list(prefix, { limit: 100 });

  if (error || !listed?.length) return;

  const paths = listed.map((f) => `${prefix}/${f.name}`);
  await supabase.storage.from(BLOG_MEDIA_BUCKET).remove(paths);
}

/**
 * Convert a YouTube or Vimeo watch/share URL to an embeddable iframe src.
 * Returns null if the URL is not a supported host.
 */
export function parseVideoEmbed(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }

  const host = parsed.hostname.replace(/^www\./, "").toLowerCase();

  // YouTube: youtube.com/watch?v=, youtu.be/, youtube.com/embed/, youtube.com/shorts/
  if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
    const shorts = parsed.pathname.match(/^\/shorts\/([a-zA-Z0-9_-]{6,})/);
    if (shorts) return `https://www.youtube-nocookie.com/embed/${shorts[1]}`;

    const embed = parsed.pathname.match(/^\/embed\/([a-zA-Z0-9_-]{6,})/);
    if (embed) return `https://www.youtube-nocookie.com/embed/${embed[1]}`;

    const v = parsed.searchParams.get("v");
    if (v && /^[a-zA-Z0-9_-]{6,}$/.test(v)) {
      return `https://www.youtube-nocookie.com/embed/${v}`;
    }
    return null;
  }

  if (host === "youtu.be") {
    const id = parsed.pathname.replace(/^\//, "").split("/")[0];
    if (id && /^[a-zA-Z0-9_-]{6,}$/.test(id)) {
      return `https://www.youtube-nocookie.com/embed/${id}`;
    }
    return null;
  }

  // Vimeo: vimeo.com/{id} or player.vimeo.com/video/{id}
  if (host === "vimeo.com") {
    const id = parsed.pathname.split("/").filter(Boolean)[0];
    if (id && /^\d+$/.test(id)) {
      return `https://player.vimeo.com/video/${id}`;
    }
    return null;
  }

  if (host === "player.vimeo.com") {
    const match = parsed.pathname.match(/^\/video\/(\d+)/);
    if (match) return `https://player.vimeo.com/video/${match[1]}`;
    return null;
  }

  return null;
}

/** Normalize media from DB (null / non-array) to a safe array. */
export function normalizeBlogMedia(raw: unknown): import("../types/blogPost").BlogMediaItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is import("../types/blogPost").BlogMediaItem => {
    if (!item || typeof item !== "object") return false;
    const m = item as { id?: unknown; type?: unknown; path?: unknown; url?: unknown };
    if (typeof m.id !== "string") return false;
    if (m.type === "image" && typeof m.path === "string") return true;
    if (m.type === "video" && typeof m.url === "string") return true;
    return false;
  });
}
